import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';
import { validateMotionSpeed } from '../../util/validators';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class PressButtonRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.PressButton', 'PressButton');
  }

  protected validate({ args }: CanonicalValidateArgs): void {
    type PBArgs = Extract<RcStep.Arguments, { argumentKind: 'PressButton' }>;
    const a = args as PBArgs;
    this.checkPrePressTarget(a);
    this.checkPressTarget(a);
    this.checkMotionSpeed(a);
  }

  /**
   * Ensure pre-press jointAngles are configured.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkPrePressTarget(
    args: Extract<RcStep.Arguments, { argumentKind: 'PressButton' }>,
  ): void {
    if (!args.prePressTarget?.jointAngles) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Pre-press position is not configured',
        fieldId: 'prePressTarget',
      });
    }
  }

  /**
   * Ensure press jointAngles are configured.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkPressTarget(
    args: Extract<RcStep.Arguments, { argumentKind: 'PressButton' }>,
  ): void {
    if (!args.pressTarget?.jointAngles) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Press position is not configured',
        fieldId: 'pressTarget',
      });
    }
  }

  /**
   * Validate speed custom limits are not using deprecated fields.
   * Throws Error when outdated configuration is detected.
   *
   * @throws {Error}
   */
  private checkMotionSpeed(
    args: Extract<RcStep.Arguments, { argumentKind: 'PressButton' }>,
  ): void {
    validateMotionSpeed('Press Button', args.motionSpeed);
  }
}

export const PressButtonValidation: ValidationRule =
  new PressButtonRule().toValidationRule();
